import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import * as Icons from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';

const { Ship, ArrowUpRight, ArrowRight } = Icons;

const applyTemplate = (str, vars) =>
  Object.entries(vars).reduce(
    (acc, [k, v]) => acc.split(`{${k}}`).join(v),
    str || ''
  );

export const InvestmentOpportunitiesPage = () => {
  const { investment, settings } = useSiteData();
  const header = settings?.page_headers?.investment || {};
  const items = (investment || []).slice().sort((a, b) => a.order - b.order);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="Investment Opportunities"
        description="Discover investment opportunities across fisheries, agriculture, ports, energy and tourism in the South East State of Somalia."
        keywords="Somalia investment, South East State investment, Somalia fisheries, Somalia agriculture, Somali ports, Somali diaspora investment"
      />
      <PageHeader
        title={header.title || 'Investment Opportunities'}
        subtitle={header.subtitle}
        crumbs={[{ label: 'Investment' }]}
      />
      <section className="bg-[#FAF6EC] py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => {
              const Icon = Icons[s.icon] || Ship;
              return (
                <Link
                  key={s.id || s.slug}
                  to={`/investment/${s.slug}`}
                  className="group flex h-full flex-col rounded-lg border border-[#E6DDC8] bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:border-[#C9A86A] hover:shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#F6F2EA] text-[#C9A86A] transition group-hover:bg-[#C9A86A] group-hover:text-white">
                      <Icon size={22} />
                    </div>
                    <ArrowUpRight size={16} className="text-[#C9A86A]" />
                  </div>
                  <h3 className="mt-5 font-serif text-[18px] font-semibold text-[#0B2545]">{s.name}</h3>
                  <p className="mt-3 text-[13.5px] leading-relaxed text-[#0B2545]/70">{s.description}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export const InvestmentSectorPage = () => {
  const { slug } = useParams();
  const { investment, settings } = useSiteData();
  const list = investment || [];
  const s = list.find((x) => x.slug === slug) || list[0];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!s) return null;
  const Icon = Icons[s.icon] || Ship;
  const tmpl = settings?.investment_sector_template || {};
  const vars = { name: s.name, name_lower: s.name.toLowerCase(), slug: s.slug };
  const paragraphs = (tmpl.paragraphs || []).map((p) => applyTemplate(p, vars));
  return (
    <>
      <SEO
        title={`Invest in ${s.name}`}
        description={s.description || `Investment opportunities in ${s.name} — South East State of Somalia.`}
        keywords={`${s.name}, Somalia ${s.name.toLowerCase()} investment, South East State, Somali economy`}
      />
      <PageHeader
        title={s.name}
        subtitle={s.description}
        crumbs={[{ label: 'Investment', to: '/investment/opportunities' }, { label: s.name }]}
      />
      <section className="bg-white py-14">
        <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#C9A86A]">
              {tmpl.eyebrow || 'Sector Overview'}
            </div>
            <h2 className="font-serif text-3xl font-bold text-[#0B2545]">
              {applyTemplate(tmpl.title_template || 'Opportunities in {name}', vars)}
            </h2>
            <div className="mt-4 h-[3px] w-16 bg-[#C9A86A]" />
            <div className="mt-6 space-y-5 text-[15px] leading-relaxed text-[#0B2545]/80">
              {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
          <aside>
            <div className="rounded-lg border border-[#E6DDC8] bg-[#FAF6EC] p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#C9A86A] text-white"><Icon size={22} /></div>
              <h4 className="mt-4 font-serif text-[17px] font-semibold text-[#0B2545]">
                {tmpl.contact_card_title || 'Connect with Investment Office'}
              </h4>
              <p className="mt-2 text-[13px] text-[#0B2545]/70">{tmpl.contact_card_description}</p>
              <Link to="/contact" className="group mt-4 inline-flex items-center gap-2 rounded-md bg-[#0B2545] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#08203a]">
                Contact Us
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};
